import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator, type FirebaseStorage } from "firebase/storage";

/**
 * Camada de infraestrutura — único ponto do projeto que conhece o SDK do Firebase.
 * Nenhum componente ou hook deve importar "firebase/*" diretamente:
 * sempre passar por este módulo ou pelos Services (ver /src/services e /src/modules/*\/services).
 */

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

function assertFirebaseConfig(): string[] {
  const required: Array<keyof typeof firebaseConfig> = [
    "apiKey",
    "authDomain",
    "projectId",
    "storageBucket",
    "appId",
  ];
  const missing = required.filter((key) => !firebaseConfig[key]);
  if (missing.length > 0 && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[firebase] Variáveis de ambiente ausentes: ${missing
        .map((k) => `VITE_FIREBASE_${k.replace(/[A-Z]/g, (m) => `_${m}`).toUpperCase()}`)
        .join(", ")}. Copie .env.example para .env.local e preencha com os dados do seu projeto.`
    );
  }
  return missing;
}

/** Chaves obrigatórias que faltaram no .env.local — usado pela UI para exibir um aviso
 *  amigável em vez de deixar chamadas ao Firebase falharem silenciosamente. */
export const firebaseMissingKeys = assertFirebaseConfig();
export const isFirebaseConfigured = firebaseMissingKeys.length === 0;

export const firebaseApp: FirebaseApp = getApps().length ? getApps()[0]! : initializeApp(firebaseConfig);

export const auth: Auth = getAuth(firebaseApp);

// Cache local persistente com suporte a múltiplas abas — habilita o
// "offline parcial" exigido pela especificação (PWA) para leituras do Firestore.
// initializeFirestore só pode ser chamado uma vez; se algo já inicializou
// (ex.: HMR em dev), caímos para getFirestore() em vez de lançar exceção
// e derrubar toda a árvore de módulos (causa clássica de tela em branco).
export const db: Firestore = (() => {
  try {
    return initializeFirestore(firebaseApp, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch {
    return getFirestore(firebaseApp);
  }
})();

export const storage: FirebaseStorage = getStorage(firebaseApp);

// Suporte a emuladores locais em desenvolvimento (opcional).
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true") {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
}
